/**
 * integrations.service.ts
 *
 * Typed API call wrappers for all integration endpoints.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface IntegrationStatus {
  connected: boolean;
}

// ─── NexHealth ────────────────────────────────────────────────────────────────

export interface NexHealthSyncResult {
  synced: number;
  records: Array<{
    biomarker_test_id: number;
    patient_id: number;
    registered_at: string;
  }>;
}

export async function nexthealthSync(practiceId: number): Promise<NexHealthSyncResult> {
  const res = await apiClient.post<{ data: NexHealthSyncResult }>(
    '/integrations/nexthealth/sync',
    { practiceId },
  );
  return res.data.data;
}

export async function nexthealthStatus(): Promise<IntegrationStatus> {
  const res = await apiClient.get<{ data: IntegrationStatus }>(
    '/integrations/nexthealth/status',
  );
  return res.data.data;
}

// ─── TouchMD ──────────────────────────────────────────────────────────────────

export interface TouchMDMediaItem {
  id: string;
  type: 'photo' | 'video' | 'document';
  url: string;
  createdAt: string;
  description: string | null;
}

export interface TouchMDPushPayload {
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  birthDate: string;
  doctorId?: number | null;
}

export async function touchmdPushPatient(
  patientId: number,
  payload: TouchMDPushPayload,
): Promise<{ result: { touchMdPatientId: string } | null }> {
  const res = await apiClient.post<{ data: { result: { touchMdPatientId: string } | null } }>(
    `/integrations/touchmd/push/${patientId}`,
    payload,
  );
  return res.data.data;
}

export async function touchmdGetMedia(patientId: number): Promise<TouchMDMediaItem[]> {
  const res = await apiClient.get<{ data: TouchMDMediaItem[] }>(
    `/integrations/touchmd/${patientId}/media`,
  );
  return res.data.data;
}

export async function touchmdGetSSOUrl(patientId: number): Promise<{ url: string }> {
  const res = await apiClient.get<{ data: { url: string } }>(
    `/integrations/touchmd/sso/${patientId}`,
  );
  return res.data.data;
}

// ─── GoHighLevel ──────────────────────────────────────────────────────────────

export interface GHLContactPayload {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
}

export async function ghlSyncPatient(
  patientId: number,
  payload: GHLContactPayload,
): Promise<{ contactId: string }> {
  const res = await apiClient.post<{ data: { contactId: string } }>(
    `/integrations/gohighlevel/sync/${patientId}`,
    payload,
  );
  return res.data.data;
}

export async function ghlStatus(): Promise<IntegrationStatus> {
  const res = await apiClient.get<{ data: IntegrationStatus }>(
    '/integrations/gohighlevel/status',
  );
  return res.data.data;
}

// ─── Google Calendar ──────────────────────────────────────────────────────────
// Per-user Google Calendar sync now lives in the Account Settings "Calendar
// Sync" tab — see googleCalendar.service.ts (endpoints under
// /settings/google-calendar). The old account-level stubs were removed.

// ─── OpenAI ───────────────────────────────────────────────────────────────────

export async function openaiSummarize(text: string): Promise<{ summary: string }> {
  const res = await apiClient.post<{ data: { summary: string } }>(
    '/integrations/openai/summarize',
    { text },
  );
  return res.data.data;
}

export async function openaiDraft(
  patientName: string,
  originalMessage: string,
): Promise<{ draft: string }> {
  const res = await apiClient.post<{ data: { draft: string } }>(
    '/integrations/openai/draft',
    { patientName, originalMessage },
  );
  return res.data.data;
}

// ─── Paid integration platform (GetLabs, etc.) ─────────────────────────────────

export interface IntegrationProduct {
  integrationProductId: number;
  key: string;
  name: string;
  description: string | null;
  price: number;
  /** Super-admin per-item floor (0 = none). */
  minPrice?: number;
  currency: string;
  /** Catalog (quote_item_costs) id so the payment modal can charge this. */
  quoteItemCostId: number;
  /** Vendor-specific metadata stored as JSON in integration_products.metadata */
  metadata?: {
    sku?: string;
    /**
     * Catalog section, e.g. 'PEPTIDE VIALS' | 'GLP-1 VIALS' | 'NASAL SPRAYS' |
     * 'SUPPLIES' | 'ANTI-EMETIC' | 'SHIPPING' (Axiom Vials Exhibit B). Catalogs
     * without categories (pens) render the flat picker.
     */
    category?: string;
    strength?: string;
    /** Recommended dose string, e.g. "12 units (360 mcg)" */
    recommendedDose?: string;
    /** Full prescribing directions / sig — LEGACY key (the pens catalog). */
    sig?: string;
    /**
     * Full prescribing directions / sig as maintained today: the Corp
     * super-admin items table's editable "Dosing instructions" column, and how
     * the Axiom Vials pricing sheet's Signature column was imported. Read as
     * the fallback for `sig` wherever suggested directions are shown or sent.
     */
    dosingInstructions?: string;
    /** Dosing frequency, e.g. "5x/week" */
    frequency?: string;
    /** Number of injections per cartridge */
    injectionsPerCart?: number;
    /** Number of weeks per cartridge */
    weeksPerCart?: number | null;
    /** Container/fill, e.g. "3 mL cartridge (300 u)" */
    fillSize?: string;
    /** Beyond-use date in days */
    budDays?: number;
    /**
     * Vials-catalog spellings of the four specs above. The pens seeder and the
     * Axiom Vials pricing-sheet import name the same facts differently, so
     * anything displaying specs must accept either — see SPEC_LINES in
     * AxiomOrderModal.
     *  - `concentration` ↔ `strength`
     *  - `dosesPerVial`  ↔ `injectionsPerCart`
     *  - `daysSupplied`  ↔ `weeksPerCart`   (already a phrase, e.g. "4 weeks")
     *  - `bud`           ↔ `budDays`        (days, as a string)
     */
    /**
     * Practice-assigned marketing name for the item (e.g. "PRIME" for
     * CJC-1295 / Ipamorelin). Displayed ALONGSIDE `name`, never instead of it —
     * a brand alone would hide what is actually being prescribed. Only the
     * eight named Axiom pens carry one; everything else is undefined.
     */
    brandName?: string;
    concentration?: string;
    dosesPerVial?: number | string;
    daysSupplied?: string;
    bud?: number | string;
    /** Total drug per cartridge, e.g. "9 mg" */
    totalMg?: string;
    /**
     * Add-on device support (e.g. Axiom requires an injection pen). Set by the
     * Corp super-admin per item:
     *  - `isAddOn`       → this product IS the add-on device (the pen).
     *  - `requiresAddOn` → ordering this product requires the add-on to be on
     *                      hand (patient owns it) or purchased in the order.
     *  - `addOnLabel`    → human label for the add-on, e.g. "pen".
     * When unset, the order UI falls back to a name match (see integrationAddOns).
     */
    isAddOn?: boolean;
    requiresAddOn?: boolean;
    addOnLabel?: string;
    [key: string]: unknown;
  } | null;
}

export interface EnabledIntegration {
  key: string;
  title: string;
  moduleId: number;
  /** 'charge_patient' | 'log_for_settlement' */
  billingModes: string[];
  products: IntegrationProduct[];
  /** When true, the order UI must collect payment before creating the request. */
  paymentEnforced?: boolean;
  /** Flat per-order shipping fee the server adds to the patient charge. */
  shippingPrice?: number;
  /**
   * Minutes an order sits in `scheduled` before it is sent to the vendor — the
   * window during which staff can still cancel it. 0 means it goes on the next
   * cron tick (staging, SCRUM-1661). Null for integrations with no such window.
   */
  submitDelayMinutes?: number | null;
}

/** One selected line item for a multi-item integration order. */
export interface OrderItemSelection {
  integrationProductId: number;
  quantity: number;
}

/** Paid integrations enabled for the current practice (per X-Target-Company). */
export async function getEnabledIntegrations(): Promise<EnabledIntegration[]> {
  const res = await apiClient.get<{ data: EnabledIntegration[] }>('/integrations/enabled');
  return res.data.data;
}

export interface IntegrationOrder {
  integrationOrderId: number;
  /**
   * The CATALOG row behind the order — for a prescription integration this is a
   * single placeholder ("Partell (RequestMyMeds)"), i.e. the vendor's name, not
   * the medicine. Read `items` for what was actually ordered.
   */
  productName: string | null;
  /**
   * What the order actually contains, from its own line rows. Present for every
   * integration; empty only for an order that genuinely has no lines.
   */
  items?: Array<{
    name: string;
    /** The catalog product the line is for, and what it is charged. */
    integrationProductId?: number | null;
    amount?: number;
    quantity: string;
    sig?: string | null;
    refills?: string | null;
    daySupply?: string | null;
    icd10?: string | null;
    dispenseAsWritten?: string | null;
    shipType?: string | null;
    shipRate?: string | null;
    /** Per-line vendor state — one script can be accepted while another is not. */
    status?: string | null;
    trackingNumber?: string | null;
    /** Partell: the script's prescriber name (from its prescriber snapshot). */
    prescriber?: string | null;
  }>;
  billingMode: string;
  amount: number;
  currency: string;
  status: string;
  fulfillmentStatus: string | null;
  /**
   * The vendor's id for this order, set only once they ACCEPTED it. Null means
   * nothing reached the pharmacy — which is what makes a failed order safe to
   * re-send as itself rather than as a new order.
   */
  externalRef?: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string | null;
  /** Staff member who placed the order (resolved from orderedByUserId). */
  orderedBy?: string | null;
  /** The staff user behind orderedBy (account order list only). */
  orderedByUserId?: number | null;
  /** Present only on the account-wide list (global Peptides page). */
  patientId?: number;
  patientName?: string | null;
  /**
   * Facility the order was placed under, stamped once at creation. Account-wide
   * list only, and NULL when no facility could be resolved for the patient —
   * those orders are real and must stay reachable, which is why the Peptides
   * page's facility filter carries an explicit "No facility" option. No facility
   * NAME is returned: the page already loads the facility list for its filter
   * and labels the id itself.
   */
  facilityId?: number | null;
  /**
   * Which client placed the order, stamped at creation from the `X-Client`
   * request header: 'webapp' (this app), 'mobile' (patient app) or 'nexus'
   * (Health Nexus).
   *
   * NULL/absent means UNTAGGED, never 'webapp' — orders placed before the
   * column existed have no source, and mobile/Nexus live in other repositories
   * that do not send the header yet. Render it as an em dash; never label an
   * untagged order with a channel.
   */
  source?: string | null;
  /** Vendor (integration) behind the order — 'axiom' etc. Account-wide list only. */
  vendorKey?: string | null;
  vendorLabel?: string | null;
  /**
   * Payments catalog category (`quote_item_costs_category`) this order's charge
   * belongs to — the SAME category the resulting payment lands in, resolved
   * server-side from the integration's catalog item, so this list and the
   * Payments screen cannot disagree about what a peptide order is.
   *
   * NULL only for an account whose integration catalog item predates
   * categorisation and has not been re-resolved since. Account-wide list only.
   */
  categoryId?: number | null;
  categoryTitle?: string | null;
  /**
   * The patient charge's ledger status (integration_charges, not `status`,
   * which is always 'placed'): pending | paid | collected | logged | comped.
   * Null when the order has no charge row. SCRUM-2200.
   */
  paymentStatus?: string | null;
  /** When a paid/collected charge was marked paid; null otherwise. */
  paidAt?: string | null;
  /** Delivered to the clinic rather than the patient — the only orders a patient picks up. */
  shipsToClinic?: boolean;
  /** Clinic handoff stamps (mirrors metadata.handoff). */
  handoff?: OrderHandoff | null;
}

/** One clinic-handoff stamp: when, and by whom. */
export interface OrderHandoffStamp {
  at: string;
  byUserId?: number | null;
  byName?: string | null;
}

/** The clinic's handoff steps for a peptide order (SCRUM-2200). Absent key = not done. */
export interface OrderHandoff {
  reconstituted?: OrderHandoffStamp;
  pickedUp?: OrderHandoffStamp;
}

export type OrderHandoffStep = keyof OrderHandoff;

// ─── Axiom vendor support tickets ──────────────────────────────────────────
// Contact AXIOM about a specific order — distinct from our own end-user
// support_tickets/Jira-sync system. Axiom is the system of record for the
// actual thread, but we read/reply to it through our own sidebar (getAxiomTicketDetail/
// postAxiomTicketMessage), not their embedded widget — their /support-sessions
// endpoint mints one session per ACCOUNT regardless of ticket_id, so it can't
// actually show a single ticket's thread. See AxiomTicketsController's docblock.

export interface AxiomSupportTicket {
  axiomSupportTicketId: number;
  axiomTicketId: number;
  subject: string;
  status: string;
  created_at: string;
}

export interface AxiomTicketMessage {
  id: number;
  author: string;
  body: string;
  created_at: string;
}

export interface AxiomTicketDetail {
  id: number;
  subject: string;
  category: string;
  priority: string;
  status: string;
  order_id: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export async function listAxiomTickets(patientId: number, orderId: number): Promise<AxiomSupportTicket[]> {
  const res = await apiClient.get<{ data: { tickets: AxiomSupportTicket[] } }>(
    `/patients/${patientId}/integration-orders/${orderId}/axiom-tickets`,
  );
  return res.data.data.tickets;
}

export async function createAxiomTicket(
  patientId: number,
  orderId: number,
  subject: string,
  description: string,
): Promise<AxiomSupportTicket> {
  const res = await apiClient.post<{ data: { ticket: AxiomSupportTicket } }>(
    `/patients/${patientId}/integration-orders/${orderId}/axiom-tickets`,
    { subject, description },
  );
  return res.data.data.ticket;
}

export async function getAxiomTicketDetail(
  patientId: number,
  orderId: number,
  ticketId: number,
): Promise<{ ticket: AxiomTicketDetail; messages: AxiomTicketMessage[] }> {
  const res = await apiClient.get<{ data: { ticket: AxiomTicketDetail; messages: AxiomTicketMessage[] } }>(
    `/patients/${patientId}/integration-orders/${orderId}/axiom-tickets/${ticketId}/detail`,
  );
  return res.data.data;
}

export async function postAxiomTicketMessage(
  patientId: number,
  orderId: number,
  ticketId: number,
  message: string,
): Promise<void> {
  await apiClient.post(
    `/patients/${patientId}/integration-orders/${orderId}/axiom-tickets/${ticketId}/messages`,
    { message },
  );
}

/** One Axiom ticket of the account, with the order and patient it belongs to. */
export interface AxiomAccountTicket {
  axiomSupportTicketId: number;
  axiomTicketId: number;
  subject: string;
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
  integrationOrderId: number;
  patientId: number;
  patientName: string | null;
  patientDisplayId: string | null;
  /** Axiom replied on THIS ticket and nobody has opened it yet. */
  unread: boolean;
  /** The order's facility (integration_orders.facilityId), for the page filter. */
  facilityId: number | null;
}

export interface AxiomAccountTicketList {
  tickets: AxiomAccountTicket[];
  /** More tickets exist than `limit`; the oldest activity is left out. */
  truncated: boolean;
  limit: number;
}

// Every Axiom ticket this account opened from an order — the Peptides page's
// "All tickets" list (SCRUM-2147). It replaced Axiom's embedded widget, whose
// "New ticket" raised tickets tied to no order.
export async function getAxiomAccountTickets(): Promise<AxiomAccountTicketList> {
  const res = await apiClient.get<{ data: AxiomAccountTicketList }>('/integrations/axiom/tickets');
  return res.data.data;
}

export async function getPatientIntegrationOrders(
  patientId: number,
  key?: string,
): Promise<IntegrationOrder[]> {
  const res = await apiClient.get<{ data: IntegrationOrder[] }>(
    `/patients/${patientId}/integration-orders`,
    { params: key ? { key } : undefined },
  );
  return res.data.data;
}

/**
 * Account-wide integration orders across ALL patients (global Peptides page).
 * Rows carry patientId/patientName + vendorKey/vendorLabel on top of the
 * per-patient shape. Optionally filtered by vendor key (e.g. 'axiom').
 */
/**
 * The account's orders for one integration. `params` are a list page's
 * filters (buildApiParams: `filtersArray` + `search`), resolved by the API
 * through the `integration_order` filter pipeline; without them, every order.
 */
export async function getAccountIntegrationOrders(
  key?: string,
  params?: { filtersArray?: string; search?: string },
): Promise<IntegrationOrder[]> {
  const res = await apiClient.get<{ data: IntegrationOrder[] }>(
    '/integration-orders',
    { params: key || params ? { ...(key ? { key } : {}), ...(params ?? {}) } : undefined },
  );
  return res.data.data;
}

/** Filter options for the order pages and Marketplace Orders lists, by canonical key. */
export async function getIntegrationOrderFilterOptions(
  key?: string,
): Promise<Record<string, Array<{ value: string; label: string }>>> {
  const res = await apiClient.get<{ data: Record<string, Array<{ value: string; label: string }>> }>(
    '/integration-orders/options',
    { params: key ? { key } : undefined },
  );
  return res.data.data ?? {};
}

export interface CreateIntegrationOrderResult {
  integrationOrderId: number;
  status: string;
  fulfillmentStatus: string | null;
  billingMode: string;
  amount: number;
  currency: string;
  /** Partell: the scripts as billed (same shape as IntegrationOrder.items). */
  items?: IntegrationOrder['items'];
}

/**
 * One prescription line for Partell (RMM). Unlike every other integration,
 * these are not catalog picks — the prescriber types a real script, so the
 * fields are the pharmacy's, not ours. All strings because that is what the
 * vendor's API takes, quantities included.
 */
export interface RmmRxItemPayload {
  /** The catalog product the script is for — what the server prices it at. */
  integrationProductId?: number;
  drug: string;
  quantity: string;
  refills: string;
  sig: string;
  daySupply: string;
  /**
   * A diagnosis code. Optional on the vendor's contract (API v2.2) and required
   * for a controlled substance — which the server decides from the drug name.
   * Omitted, not sent as "", when there is nothing to send.
   */
  icd10?: string;
  dispenseAsWritten: 'Yes' | 'NO';
}

/**
 * Where one RMM order ships. One order is one shipment -- its lines are grouped
 * under a shared `prescription_order_id` and the pharmacy is told `num_scripts`
 * -- so this is decided once and the server writes it onto every line. The
 * vendor's contract is still per-line; only where the decision is made moved.
 */
export interface RmmShippingPayload {
  shipType: 'ship_to_patient' | 'ship_to_clinic';
  shipRate: '2_day' | 'overnight';
  /** Required when shipType is ship_to_clinic. */
  clinicShipAddress?: { line1?: string; city?: string; state?: string; zip?: string } | null;
}

/**
 * One prescription read off an uploaded document. Every field is a string and
 * may be empty: the reader leaves what it could not read blank rather than
 * guessing, so the form shows the gap and its validation blocks the submit.
 */
export interface RmmExtractedRx {
  drug: string;
  sig: string;
  quantity: string;
  refills: string;
  day_supply: string;
  icd10: string;
  dispense_as_written: string;
  notes: string;
}

/**
 * Read the prescriptions off a scanned or exported script to PREFILL the form.
 * Saves nothing and sends nothing to the pharmacy — the prescriber confirms
 * each field and submits separately.
 */
export async function extractRmmPrescription(file: File): Promise<RmmExtractedRx[]> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiClient.post<{ data: { prescriptions: RmmExtractedRx[] } }>(
    '/integrations/rmm/extract-prescription',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data.data?.prescriptions ?? [];
}

/** The one prescriber the whole order is written under. */
export interface RmmPrescriberPayload {
  userId?: number | null;
  npi: string;
  dea?: string | null;
  firstName: string;
  lastName: string;
  address: { line1: string; city: string; state: string; zip: string };
  phone: string;
  fax?: string | null;
  clinicName?: string | null;
}

export interface LabOrderFilePayload {
  name: string;
  /** base64 (no data: prefix) — backend uploads to the vendor. */
  dataBase64: string;
}

export interface LabOrderDetailsPayload {
  contactName?: string | null;
  contactPhone?: string | null;
  testCodes?: string[];
  file?: LabOrderFilePayload | null;
}

export async function createIntegrationOrder(
  patientId: number,
  payload: {
    key: string;
    /** Multi-item selection. Omit for single-item integrations (GetLabs). */
    items?: OrderItemSelection[];
    billingMode?: string;
    metadata?: Record<string, unknown>;
    paymentMethodId?: string;
    labOrderDetails?: LabOrderDetailsPayload;
    /**
     * Partell (RMM) only. The server dispatches on `key === 'rmm'` BEFORE the
     * generic items[] flow, because a prescription has no catalog product to
     * price against — so these travel instead of `items`, not alongside them.
     */
    rxItems?: RmmRxItemPayload[];
    prescriber?: RmmPrescriberPayload;
    /** Destination and speed for the whole order. */
    shipType?: RmmShippingPayload['shipType'];
    shipRate?: RmmShippingPayload['shipRate'];
    clinicShipAddress?: RmmShippingPayload['clinicShipAddress'];
    /**
     * Where an RMM order ships when it ships to the patient.
     *
     * Same key and shape Axiom writes, so one form serves both integrations
     * and the order debug panel reads one place. Absent when the order ships
     * to the clinic, which carries its own address instead.
     */
    shippingAddress?: {
      /** Who receives it — defaults to the patient when blank. */
      recipientName?: string;
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
      /** The saved book row staff picked; the server upserts and overwrites it. */
      patientShippingAddressId?: number;
    };
  },
): Promise<CreateIntegrationOrderResult> {
  const res = await apiClient.post<{ data: CreateIntegrationOrderResult }>(
    `/patients/${patientId}/integration-orders`,
    payload,
  );
  return res.data.data;
}

/** Edit an existing order in place (PATCH). Recomputes items + amount and the
 *  still-pending patient charge; does not re-bill or re-send to the vendor. */
export async function updateIntegrationOrder(
  patientId: number,
  orderId: number,
  payload: {
    key: string;
    items?: OrderItemSelection[];
    metadata?: Record<string, unknown>;
    /**
     * Partell (RMM) only: the one thing a prescription edit may change — where
     * it ships. The server refuses everything else on an RMM order.
     */
    shippingAddress?: {
      recipientName?: string;
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
      patientShippingAddressId?: number;
    };
  },
): Promise<CreateIntegrationOrderResult> {
  const res = await apiClient.patch<{ data: CreateIntegrationOrderResult }>(
    `/patients/${patientId}/integration-orders/${orderId}`,
    payload,
  );
  return res.data.data;
}

/** Orders are cancellable only within this many minutes of creation. */
export const ORDER_CANCEL_WINDOW_MINUTES = 10;

/** Cancel an integration order (server enforces the 10-minute window). */
export async function cancelIntegrationOrder(
  patientId: number,
  orderId: number,
  key: string,
): Promise<{ integrationOrderId: number; status: string; refundNeeded?: boolean }> {
  const res = await apiClient.post<{ data: { integrationOrderId: number; status: string; refundNeeded?: boolean } }>(
    `/patients/${patientId}/integration-orders/${orderId}/cancel`,
    { key },
  );
  return res.data.data;
}

export interface RetryIntegrationOrderResult {
  integrationOrderId: number;
  status: string;
  fulfillmentStatus: string;
  externalRef: string | null;
  retryCount: number;
  /** The vendor accepted it this time. False means it failed again — `error` says why. */
  accepted: boolean;
  error: string | null;
}

/**
 * Re-send a FAILED order to the pharmacy. Distinct from a re-order: no new order
 * and no second charge — the same order is pushed again once whatever rejected
 * it has been fixed. The server refuses it for any order that already reached
 * the vendor, so this can never duplicate a prescription.
 *
 * Resolves even when the vendor rejects it again; check `accepted`.
 */
export async function retryIntegrationOrder(
  patientId: number,
  orderId: number,
): Promise<RetryIntegrationOrderResult> {
  const res = await apiClient.post<{ data: RetryIntegrationOrderResult }>(
    `/patients/${patientId}/integration-orders/${orderId}/retry`,
    {},
  );
  return res.data.data;
}

export interface IntegrationOrderDefaults {
  providerName: string;
  providerPhone: string;
  /** NPI from aa_accounts_quest_providers for the patient's plan doctor. */
  providerNpi: string;
  /** DEA from the doctors table for the patient's plan doctor. */
  providerDea: string;
  /** Credential (MD/DO/NP/PA) from the doctor/user suffix; '' if unknown. */
  providerCredential: string;
  /** Practice (facility) address — the patient's plan facility, else the account
   *  default facility. A single free-text line (line1); city/state/zip are blank. */
  providerAddressLine1: string;
  providerCity: string;
  providerState: string;
  providerZip: string;
  /** Patient demographics pre-populated from their profile. Editable by staff. */
  firstName: string;
  lastName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

export async function getIntegrationOrderDefaults(patientId: number, key: string): Promise<IntegrationOrderDefaults> {
  const res = await apiClient.get<{ data: IntegrationOrderDefaults }>(
    `/patients/${patientId}/integrations/${key}/order-defaults`,
  );
  return res.data.data;
}

export interface IntegrationServiceability {
  serviceable: boolean;
  zip: string;
  unknown?: boolean;
  reason?: string;
  area?: { city?: string; state?: string; timezone?: string } | null;
}

/** Pre-flight check that the patient's zip is in the vendor's service area. */
export async function getIntegrationServiceability(patientId: number, key: string): Promise<IntegrationServiceability> {
  const res = await apiClient.get<{ data: IntegrationServiceability }>(
    `/patients/${patientId}/integrations/${key}/serviceability`,
  );
  return res.data.data;
}

export interface IntegrationCoverage {
  covered: boolean;
  membership: { id: number; title: string } | null;
}

/**
 * Whether the patient has an active membership the account has marked as
 * entitling free visits for this integration.
 */
export async function getIntegrationCoverage(patientId: number, key: string): Promise<IntegrationCoverage> {
  const res = await apiClient.get<{ data: IntegrationCoverage }>(`/patients/${patientId}/integrations/${key}/coverage`);
  return res.data.data;
}

/** An existing V4 payments-table row the server deems linkable to an order. */
export interface EligibleOrderPayment {
  paymentID: number;
  date: string;
  /** Effective amount the payment covered: cash (curAmount) + gift card. */
  amount: number;
  /** Portion funded by gift-card redemptions; 0 for cash-only payments. */
  giftCardAmount: number;
  payType: string | null;
  payMethod: string | null;
  refNumber: string | null;
  memo: string | null;
}

export interface EligibleOrderPaymentsResult {
  /** The pending charge's amount, or null when the order has no pending charge. */
  amountDue: number | null;
  /** Set when payments is empty because there's nothing left to pay. */
  reason?: 'no_pending_charge';
  payments: EligibleOrderPayment[];
}

/**
 * Existing patient payments that could cover an order's pending charge —
 * recent, non-void, ≥ the amount due, and not already linked to another
 * integration order. Feeds the "use an existing payment" chooser.
 */
export async function getEligibleOrderPayments(
  patientId: number,
  orderId: number,
): Promise<EligibleOrderPaymentsResult> {
  const res = await apiClient.get<{ data: EligibleOrderPaymentsResult }>(
    `/patients/${patientId}/integration-orders/${orderId}/eligible-payments`,
  );
  return res.data.data;
}

export interface MarkOrderPaidResult {
  integrationOrderId: number;
  paid: boolean;
  fulfillmentStatus: string | null;
  /** True when this payment released an order held by the payment gate. */
  fulfilled: boolean;
  /** The existing payment consumed, when paid via `paymentID` linking. */
  linkedPaymentId?: number | null;
}

/**
 * Confirm a charge_patient order's fee was collected. Two paths:
 *  - default (or `paymentRef`): the Forward "take a payment" flow already ran;
 *    optionally records the gateway reference.
 *  - `paymentID`: link an EXISTING payments row instead of charging anew (the
 *    server validates ownership/void/amount/single-use — 422s on failure).
 * For an order held in 'awaiting_payment' this is also what releases it to the
 * vendor — the server starts the grace window from here, not from order creation.
 */
export async function markIntegrationOrderPaid(
  patientId: number,
  orderId: number,
  opts?: {
    paymentRef?: string;
    /** Link a PRE-EXISTING payment. Server checks the amount matches exactly. */
    paymentID?: number;
    /**
     * A payment just taken for this order. Attaches it so it is spent and can't
     * be offered again for the next order (SCRUM-1401) — no amount check, since
     * we created it for this charge moments ago.
     */
    collectedPaymentID?: number;
  },
): Promise<MarkOrderPaidResult> {
  const res = await apiClient.post<{ data: MarkOrderPaidResult }>(
    `/patients/${patientId}/integration-orders/${orderId}/mark-paid`,
    {
      paymentRef: opts?.paymentRef,
      paymentID: opts?.paymentID,
      collectedPaymentID: opts?.collectedPaymentID,
    },
  );
  return res.data.data;
}

export interface OrderHandoffResult {
  integrationOrderId: number;
  /** False when the order was already in the asked-for state (the original stamp is kept). */
  changed: boolean;
  paymentStatus: string | null;
  paidAt: string | null;
  shipsToClinic: boolean;
  handoff: OrderHandoff;
}

/**
 * Mark (done=true) or undo (done=false) a clinic-handoff step on an order.
 * Allowed in any state but cancelled; `pickedUp` only on clinic-delivered
 * orders (422 otherwise).
 */
export async function setOrderHandoff(
  patientId: number,
  orderId: number,
  step: OrderHandoffStep,
  done: boolean,
): Promise<OrderHandoffResult> {
  const res = await apiClient.post<{ data: OrderHandoffResult }>(
    `/patients/${patientId}/integration-orders/${orderId}/handoff`,
    { step, done },
  );
  return res.data.data;
}

export interface IntegrationConnection {
  hostname: string;
  clientIdConfigured: boolean;
  apiTokenConfigured: boolean;
  webhookSecretConfigured: boolean;
}

export interface IntegrationSettings {
  key: string;
  title: string;
  billingMode: string;
  billingModes: string[];
  price: number;
  currency: string;
  quoteItemCostId: number;
  connection?: IntegrationConnection;
  /** Active holders of these membership templates get free visits (comped). */
  entitledMembershipIds: number[];
  /** Super-admin floor (Prospera-Corp-App). 0 = no minimum. */
  minCustomerPrice: number;
  /** Per-integration checkout mode (practice-set). */
  paymentEnforced?: boolean;
  /** Per-item catalog for multi-item integrations (admin price editor). */
  items?: IntegrationSettingsItem[];
}

/** A catalog item in the practice admin price editor. */
export interface IntegrationSettingsItem {
  integrationProductId: number;
  key: string;
  name: string;
  description: string | null;
  enabled: boolean;
  customerPrice: number;
  minPrice: number;
  currency: string;
  /**
   * Prospera-set price the location may not change (the Axiom Vials shipping
   * fee, SCRUM-2023). Shown read-only and never sent on save; the server
   * refuses a changed price for it (SHIPPING_PRICE_LOCKED).
   */
  priceLocked?: boolean;
}

/** A BodySpec scan location near the patient (subset of the vendor's payload). */
export interface BodySpecLocation {
  id?: string;
  name?: string;
  location_type?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  distance_miles?: number;
  [k: string]: unknown;
}

/** An open BodySpec appointment slot. */
export interface BodySpecSlot {
  appt_id?: string;
  start_time?: string;
  end_time?: string;
  [k: string]: unknown;
}

/** BodySpec: scan locations near the patient's zip. */
export async function getBodyspecLocations(
  patientId: number,
): Promise<{ locations: BodySpecLocation[]; configured?: boolean; zip?: string; reason?: string }> {
  const res = await apiClient.get<{ data: { locations: BodySpecLocation[]; configured?: boolean; zip?: string; reason?: string } }>(
    `/patients/${patientId}/integrations/bodyspec/locations`,
  );
  return res.data.data;
}

/** A SiPhox kit type the account can order, with its remaining credit balance. */
export interface SiphoxKitType {
  type: string;
  /** Display name resolved from the matching integration product (e.g. "Core Health"). */
  name: string;
  /** integrationProductId of the matching product, used to look up price. Null when unmatched. */
  integrationProductId: number | null;
  credits: number;
}

/** SiPhox: kit types the account can order (from SiPhox /credits). */
export async function getSiphoxKitTypes(): Promise<{ kitTypes: SiphoxKitType[]; configured?: boolean }> {
  const res = await apiClient.get<{ data: { kitTypes: SiphoxKitType[]; configured?: boolean } }>(
    '/integrations/siphox/kit-types',
  );
  return res.data.data;
}

export interface TruDiagnosticSku {
  id: string;
  name: string;
  description: string;
}

/** TruDiagnostic: live SKU catalog from the vendor API (no per-practice config;
 *  the API exposes no prices — Prospera's % markup is applied at order time). */
export async function getTruDiagnosticSkus(): Promise<{ skus: TruDiagnosticSku[]; configured?: boolean; error?: string }> {
  const res = await apiClient.get<{ data: { skus: TruDiagnosticSku[]; configured?: boolean; error?: string } }>(
    '/integrations/trudiagnostic/skus',
  );
  return res.data.data;
}

/** One scored test, as MescreenIntegration::summarizeTests() stores it. */
export interface MescreenTestSummary {
  testId: string;
  testDate: string;
  profile: string;
  scores: Array<{ group: string; title: string; value: number | null; max: number | null; unit: string; level: string }>;
}

/**
 * mescreen results PDF. The server fetches it live from mescreen on every
 * request (it is never stored), so this needs the authed client, not a link.
 */
export async function getMescreenResultsPdf(patientId: number, orderId: number): Promise<Blob> {
  const res = await apiClient.get(`/patients/${patientId}/integration-orders/${orderId}/mescreen/results-pdf`, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

/** One entry of an order's activity log (integration_order_events). */
export interface IntegrationOrderEvent {
  action: 'created' | 'resent' | 'resent_as' | 'cancelled' | 'retried' | 'submitted' | string;
  userId: number | null;
  userName: string | null;
  details: Record<string, unknown> | null;
  at: string;
}

/** Who did what to an order, oldest first. */
export async function getIntegrationOrderEvents(patientId: number, orderId: number): Promise<IntegrationOrderEvent[]> {
  const res = await apiClient.get<{ data: { events: IntegrationOrderEvent[] } }>(
    `/patients/${patientId}/integration-orders/${orderId}/events`,
  );
  return res.data.data?.events ?? [];
}

export interface GenovaProduct {
  id: number;
  name: string;
  /** Genova test code from the US fee schedule; null when not matched. */
  code?: string | null;
  /** CASH price from the published Genova US fee schedule; null when unknown. */
  price?: number | null;
}

/** Genova: live product catalog from the Ordering API. Large catalog — pass a
 *  wildcard `search` to filter by name. */
export async function getGenovaProducts(search?: string): Promise<{ products: GenovaProduct[]; configured?: boolean; error?: string }> {
  const res = await apiClient.get<{ data: { products: GenovaProduct[]; configured?: boolean; error?: string } }>(
    '/integrations/genova/products',
    { params: search ? { search } : undefined },
  );
  return res.data.data;
}

export interface IntegrationOnboardingStatus {
  onboarded: boolean;
  status?: string;
  submittedAt?: string | null;
  summary?: { legalEntityName?: string | null; contactName?: string | null; billingEmail?: string | null };
}

/** Clinic onboarding/enrollment status for an integration that gates use behind
 *  it (Axiom Connect). */
export async function getIntegrationOnboarding(key: string): Promise<IntegrationOnboardingStatus> {
  const res = await apiClient.get<{ data: IntegrationOnboardingStatus }>(`/integrations/${key}/onboarding`);
  return res.data.data;
}

export interface SubmitOnboardingResult {
  onboarded: boolean;
  submittedAt?: string;
  /** Axiom only: whether the signed-terms attestation reached Axiom (POST /clinics).
   *  `false` means the local enrollment saved but the Axiom sync didn't go through
   *  (the first order will retry it). Absent for integrations without an enroll API. */
  axiomEnrolled?: boolean;
  /** Clinic-facing reason the Axiom enroll didn't complete, when axiomEnrolled is false. */
  axiomError?: string;
}

/** Submit the clinic onboarding enrollment (stored encrypted server-side). For
 *  Axiom this also forwards the signed-terms attestation to Axiom's /clinics API. */
export async function submitIntegrationOnboarding(
  key: string,
  payload: Record<string, unknown>,
): Promise<SubmitOnboardingResult> {
  const res = await apiClient.post<{ data: SubmitOnboardingResult }>(
    `/integrations/${key}/onboarding`,
    payload,
  );
  return res.data.data;
}

export interface IntegrationBanking {
  exists: boolean;
  bankName?: string | null;
  accountHolderName?: string | null;
  accountType?: string | null;
  /** Trailing 4 digits only — the API never returns the full numbers. */
  routingLast4?: string | null;
  accountLast4?: string | null;
  updatedAt?: string | null;
}

/** The clinic's stored ACH details, secrets masked to last-4. */
export async function getIntegrationBanking(key: string): Promise<IntegrationBanking> {
  const res = await apiClient.get<{ data: IntegrationBanking }>(`/integrations/${key}/banking`);
  return res.data.data;
}

export interface UpdateBankingPayload {
  bankName: string;
  accountHolderName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string;
  achAuthorization: true;
}

export interface UpdateBankingResult {
  saved: boolean;
  routingLast4?: string | null;
  accountLast4?: string | null;
  updatedAt?: string;
  /** Whether the change reached the vendor. `false` means it saved locally and
   *  the next order retries; absent when the vendor has no enroll API. */
  synced?: boolean;
  syncError?: string;
}

/** Replace the ACH block. Every field is required — this re-attests the
 *  authorization rather than patching one value. */
export async function updateIntegrationBanking(
  key: string,
  payload: UpdateBankingPayload,
): Promise<UpdateBankingResult> {
  const res = await apiClient.put<{ data: UpdateBankingResult }>(
    `/integrations/${key}/banking`,
    payload,
  );
  return res.data.data;
}

export interface AxiomCatalogItem {
  sku: string;
  name: string;
  strength?: string;
  formFactor?: string;
  imageUrl?: string | null;
  /**
   * ISO back-order date; null or past = in stock.
   *
   * NOT SUFFICIENT ON ITS OWN. Axiom currently leaves this null on every entry
   * and expresses back-orders through `backordered` + `expectedBackAt` instead.
   * Read the two together — see AxiomCatalogIndex on the server.
   */
  unavailableUntil?: string | null;
  /** Back-order flag. Set today where `unavailableUntil` is not. */
  backordered?: boolean;
  /** ISO date the item is expected back, alongside `backordered`. */
  expectedBackAt?: string | null;
  /**
   * Other identifiers this entry answers to. Axiom runs two SKU conventions —
   * short codes and slugs — and these bridge them, so a lookup that ignores
   * aliases misses roughly half the vials catalog.
   */
  aliases?: string[];
  /**
   * 2-letter state codes Axiom will ship THIS product into — per-product, not
   * the same thing as whether Axiom's pharmacy network covers a state at all.
   * Absent = Axiom hasn't told us anything for this item (unrestricted, fail
   * open). An explicit EMPTY array means Axiom ships it nowhere right now —
   * see isInvalidatedProduct() / availableStatesFor() in AxiomOrderModal.
   */
  availableStates?: string[];
}

/** Live Axiom catalog (sku, name, imageUrl, …) for product images in the order UI.
 *  `key` selects the Axiom-family integration ('axiom' pens, 'axiom_vials' vials);
 *  the backend route accepts either family module. */
export async function getAxiomCatalog(key: string = 'axiom'): Promise<{ catalog: AxiomCatalogItem[]; error?: string }> {
  const res = await apiClient.get<{ data: { catalog: AxiomCatalogItem[]; error?: string } }>(`/integrations/${key}/catalog`);
  return res.data.data;
}

/**
 * One Axiom operational announcement (GET /notices; notice.published /
 * notice.withdrawn webhooks) — a SKU going backordered, a holiday changing
 * when orders ship. Axiom did not give a wire schema for this beyond prose,
 * so every field but `id` is optional and read defensively; render whatever
 * is present rather than assuming a rigid shape.
 */
export interface AxiomNotice {
  id: string;
  /** Free text — title/short label, when Axiom sends one. */
  title?: string;
  /** Free text — the announcement body. */
  message?: string;
  /** 'info' | 'warning' | anything else Axiom sends; unrecognized falls back to info styling. */
  severity?: string;
  /** SKUs this notice is about, when it's item-specific rather than general (e.g. a holiday). */
  skus?: string[];
}

/**
 * Currently active notices (already filtered server-side: not withdrawn, not
 * past `endsAt`) for the order modal's info/warning banners. Same
 * either-family-key route shape as getAxiomCatalog.
 */
export async function getAxiomNotices(key: string = 'axiom'): Promise<{ notices: AxiomNotice[] }> {
  const res = await apiClient.get<{ data: { notices: AxiomNotice[] } }>(`/integrations/${key}/notices`);
  return res.data.data;
}

/**
 * The clinic's Axiom standing, read before ordering (SCRUM-1805).
 *
 * `known` is false when the balance could not be read — the UI must not tell a
 * clinic it is in good standing on the strength of a failed lookup, so a false
 * here means "say nothing" rather than "all clear".
 *
 * Counts only: Axiom has issued no invoices to us yet, so the per-invoice
 * payload shape is unverified and amounts are deliberately not surfaced.
 */
export interface AxiomBillingStatus {
  known: boolean;
  /** Invoices Axiom has marked `late` — these are what pause ordering. */
  lateCount: number;
  /** Issued but unsettled (issued + pending + partial). */
  openCount: number;
}

export async function getAxiomBillingStatus(): Promise<AxiomBillingStatus> {
  const res = await apiClient.get<{ data: AxiomBillingStatus }>('/integrations/axiom/billing-status');
  return res.data.data;
}

/** One postal address, as any of the three sources states it. */
export interface DebugAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  recipientName?: string | null;
  phone?: string | null;
}

/**
 * What Axiom was told about an order — above all, where it is going.
 *
 * `vendorMatchesChosen` is null when the order has not reached Axiom, so the UI
 * can say "not sent yet" instead of showing a bare red "no match".
 */
export interface AxiomOrderDebug {
  partnerOrderId: string;
  externalRef: string | null;
  fulfillmentStatus: string | null;
  shipTo: {
    chosenOnOrder: DebugAddress | null;
    patientChart: DebugAddress | null;
    atVendor: DebugAddress | null;
    vendorMatchesChosen: boolean | null;
  };
  atVendor: Record<string, unknown> | null;
  vendorEvents: Array<Record<string, unknown>> | null;
  vendorError: string | null;
  /** What would be sent if the order went now — a preview, not a record. */
  outbound: {
    shipTo?: DebugAddress;
    shipToSource?: 'order' | 'chart';
    shipToWarning?: string;
    items?: Array<{ name?: string | null; sku?: string | null; strength?: string | null; quantity?: number | null }>;
    directions?: unknown;
    physician?: unknown;
    error?: string;
  };
}

export async function getAxiomOrderDebug(patientId: number, orderId: number): Promise<AxiomOrderDebug> {
  const res = await apiClient.get<{ data: AxiomOrderDebug }>(
    `/patients/${patientId}/integration-orders/${orderId}/axiom-debug`,
  );
  return res.data.data;
}

export interface NpiLookupResult {
  found: boolean;
  active?: boolean;
  name?: string;
  enumerationType?: string;
  city?: string | null;
  state?: string | null;
  error?: string;
}

/** Verify a 10-digit NPI against the public NPPES registry (server-side proxy). */
export async function lookupNpi(number: string): Promise<NpiLookupResult> {
  const res = await apiClient.get<{ data: NpiLookupResult }>('/integrations/npi-lookup', { params: { number } });
  return res.data.data;
}

/** Save a prescriber NPI onto a physician user so it's reused on every later
 *  order. Pass `userId` (from the prescriber picker) to name the physician;
 *  omitted → the patient's plan doctor (legacy behavior). */
export async function saveIntegrationProviderNpi(
  patientId: number,
  key: string,
  npi: string,
  userId?: number,
): Promise<{ saved: boolean; providerNpi: string }> {
  const res = await apiClient.patch<{ data: { saved: boolean; providerNpi: string } }>(
    `/patients/${patientId}/integrations/${key}/provider-npi`,
    { npi, ...(userId ? { userId } : {}) },
  );
  return res.data.data;
}

/** A prescriber-eligible user (Doctor/Nurse) with their stored credentials. */
export interface IntegrationPrescriber {
  userId: number;
  firstName: string;
  lastName: string;
  role: string;
  /** MD/DO/NP/PA… from the doctor/user suffix; '' if unknown. */
  credential: string;
  /** Stored NPI (aa_accounts_quest_providers) — '' when none on file yet. */
  npi: string;
  dea: string;
  phone: string;
}

/** The account's prescribers for the Rx order modals' prescriber picker. */
export async function getIntegrationPrescribers(): Promise<IntegrationPrescriber[]> {
  const res = await apiClient.get<{ data: IntegrationPrescriber[] }>('/integrations/prescribers');
  return res.data.data ?? [];
}

/** BodySpec: open slots at a chosen location. */
export async function getBodyspecAvailability(
  patientId: number,
  locationId: string,
): Promise<{ slots: BodySpecSlot[]; configured?: boolean }> {
  const res = await apiClient.get<{ data: { slots: BodySpecSlot[]; configured?: boolean } }>(
    `/patients/${patientId}/integrations/bodyspec/locations/${encodeURIComponent(locationId)}/availability`,
  );
  return res.data.data;
}

export async function getIntegrationSettings(key: string): Promise<IntegrationSettings> {
  const res = await apiClient.get<{ data: IntegrationSettings }>(`/integrations/${key}/settings`);
  return res.data.data;
}

export async function updateIntegrationSettings(
  key: string,
  payload: {
    billingMode?: string;
    price?: number;
    entitledMembershipIds?: number[];
    paymentEnforced?: boolean;
    /** Per-item customer price edits (multi-item integrations). */
    items?: Array<{ integrationProductId: number; customerPrice: number }>;
  },
): Promise<{ billingMode: string; price: number; entitledMembershipIds: number[] }> {
  const res = await apiClient.put<{ data: { billingMode: string; price: number; entitledMembershipIds: number[] } }>(
    `/integrations/${key}/settings`,
    payload,
  );
  return res.data.data;
}

export interface ReconciliationMonth {
  month: string;
  visits: number;
  owedToVendor: number;
  collected: number;
  /** Net to Prospera: collected − owedToVendor */
  ourProfit: number;
}

export interface IntegrationReconciliation {
  unitCost: number;
  currency: string;
  months: ReconciliationMonth[];
  upcomingVisits: number;
}

export async function getIntegrationReconciliation(key: string, months = 6): Promise<IntegrationReconciliation> {
  const res = await apiClient.get<{ data: IntegrationReconciliation }>(
    `/integrations/${key}/reconciliation`,
    { params: { months } },
  );
  return res.data.data;
}
